import { create } from 'zustand';
import { catalogProductExtras } from '../lib/catalogLookup';
import type { MockProduct } from '../lib/mock-data';
import type { Customer, ParkedOrderSnapshot, ReceiptDetail } from '@matix/types';
import { roundMoney } from '@matix/utils';
import { useTaxConfig } from './taxConfig';
import { useInvoiceSources } from './invoiceSources';
import { moneyPrecision } from './currency';
import { useTenantConfig } from './tenantConfig';

/**
 * Cart store — mirrors the prototype's `state` object for the active sale.
 *
 * Held in memory only. When orders/payments features land, this state is
 * what we POST to the API. Park/recall serialize this whole shape.
 */

export interface CartLineMod {
  /** mstr_ModifierOptions.ID — sent to the API for audit. */
  option_id: number;
  /** mstr_ModifierGroups.ID — required for Inv_Receipt_Line_Modifiers.GroupID. */
  group_id: number;
  group: string;
  idx: number;
  name: string;
  nameAr: string;
  /**
   * What this option adds to the line's unit price. For a "choices with
   * quantity" option it is the EXTENDED amount (unit price × qty), so every
   * sum over `mods[].price` stays right without knowing about quantities.
   */
  price: number;
  /** "Choices with quantity" only: the typed quantity (3.5 m). Absent = 1. */
  qty?: number;
}

export interface CartLine {
  /** Stable per-add id; used by qty/remove handlers. */
  cartId: string;
  product: MockProduct;
  /** Selected modifiers (snapshot at add-time). */
  mods: CartLineMod[];
  qty: number;
  /** Sum of (product.price + mods.price) — does NOT include qty. */
  unitPrice: number;
  note?: string;
  /**
   * Return/exchange: when true this line is a RETURNED item, not a sold one.
   * qty stays a positive magnitude internally; the line renders (and is sent
   * to the API) with a NEGATIVE quantity. A cart holding at least one return
   * line and at least one sale line is an exchange; all-return is a refund.
   */
  isReturn?: boolean;
  /**
   * Return/exchange: does this returned item go back on the shelf?
   *
   * Only ever surfaced (and only ever sent) for a return line whose item
   * carries `product.restockOnReturn` — those are the items the tenant wants
   * a decision on. Set when the line becomes a return, from the tenant's
   * `restockOnReturnDefault` (ticked unless the tenant turned it off);
   * unticking makes the backend write a Code 61 Destruction voucher
   * alongside the return. Unset still reads as restock.
   */
  restock?: boolean;
  /**
   * Membership redemption: when set, this line was added for free from the
   * customer's prepaid package and draws down the given Inv_MembershipDetail.
   * unitPrice is 0 and the line is sent with `membership_detail_id`.
   */
  membershipDetailId?: number | null;
  /**
   * The quantity this line had in the HELD order it was reopened from. Going
   * BELOW it is a removal and needs the manager's password; going above it is
   * an addition — what "print new items" sends to the kitchen. Absent on a
   * line added during this edit, and on every ordinary sale.
   */
  heldBaseQty?: number;
  /** Optional per-line salesmen → Inv_Receipt_d.SalesManId / SalesMan2Id. */
  salesman1Id?: number | null;
  salesman2Id?: number | null;
  /**
   * Discount on THIS line → Inv_Receipt_d.Discount_Value / Discount_Percent.
   *
   * Separate from the header `Discount` in every way that matters: it is
   * gated on `permissions.allow_item_discount`
   * (Security_Users.ShowReceiptItemDiscount) rather than
   * allow_overall_discount, and it reduces the line's own total, so it is
   * already inside the subtotal the header discount is then taken from.
   *
   * On a RETURN line (since 2026-09-28) it is a discount the cashier takes
   * off the refund by hand, on top of `refundUnitDiscount`.
   */
  lineDiscount?: LineDiscount | null;
  /**
   * RETURN lines loaded from an invoice: the original line's OWN discount,
   * per unit, carried again as this line's discount — the price stays as sold
   * (the user, 2026-09-28). The invoice-level discount is not in here; it
   * comes back as the return's whole-receipt discount (`returnHeaderShare`).
   * The server holds the refund to what was paid (`assertRefundWithinPaid`).
   * Unset on everything else.
   */
  refundUnitDiscount?: number;
  /**
   * Gift-card sale: when set, this line SELLS a gift card at `unitPrice` and
   * the order request carries a matching `gift_cards.issue[]` entry. The server
   * creates the card inside the receipt's own transaction, so the card and the
   * sale that paid for it can never exist without each other.
   *
   * The card number is deliberately absent — the server generates it, and the
   * cashier is shown it once after the charge.
   */
  giftCardIssue?: GiftCardIssueDraft | null;
  /**
   * The unit price this line had before the cashier first typed over it (the
   * catalog's, with its options). Sent as `price_override_from` so the server
   * records a price_override audit event; never changes what is charged.
   */
  priceOverrideFrom?: number;
}

/**
 * What the cashier chose in the "Sell gift card" modal. Everything except the
 * value is optional: a card bought off the shelf belongs to whoever holds it.
 */
export interface GiftCardIssueDraft {
  recipientName?: string | null;
  recipientPhone?: string | null;
  /** ISO date; omitted means "use the tenant's default expiry". */
  expiresAt?: string | null;
  /** Pre-printed physical card being loaded, rather than a generated number. */
  cardNumber?: string | null;
}

/**
 * Money taken off one line, clamped to the line's own value.
 *
 * Mirrors the server exactly (`computeLines`): value wins over percent, the
 * percent is taken off the UNROUNDED base. The clamp is the one thing the
 * server does not do for a sale line — it would happily compute a negative
 * line — so the clamped figure is what gets SENT as `discount_value`, and the
 * two arrive at the same number.
 *
 * A RETURN line (2026-09-28) carries its original line's own discount again
 * (`refundUnitDiscount` × qty) plus any discount taken off it by hand, a
 * percent of it being taken off what is left after the first. The invoice's
 * whole-receipt discount is not here — see `returnHeaderShare`.
 */
export function lineDiscountAmount(line: CartLine, rm: (n: number) => number): number {
  const d = line.lineDiscount;
  const base = line.unitPrice * line.qty;
  if (line.isReturn) {
    const inherited = Math.max(0, (line.refundUnitDiscount ?? 0) * line.qty);
    const rest = Math.max(base - inherited, 0);
    const manual = d && d.value > 0 ? (d.type === 'percent' ? (rest * d.value) / 100 : d.value) : 0;
    const total = inherited + manual;
    return total > 0 ? rm(Math.min(total, base)) : 0;
  }
  if (!d || d.value <= 0) return 0;
  const raw = d.type === 'percent' ? (base * d.value) / 100 : d.value;
  return rm(Math.min(Math.max(raw, 0), base));
}

/**
 * What one line is worth after its own discount — the number that goes on the
 * wire as `items[].total` and is summed into the subtotal. Rounded once, here,
 * so the cart, the wire builder and the server all round in the same place.
 */
export function lineNetTotal(line: CartLine, rm: (n: number) => number): number {
  return rm(line.unitPrice * line.qty - lineDiscountAmount(line, rm));
}

export interface DeliveryDetails {
  zone: string;
  fee: number;
  eta: number; // minutes
  address: string;
  phone: string;
}

export type DiscountType = 'percent' | 'amount';

/**
 * A discount on one cart line.
 *
 * `amount` is money off the WHOLE line (qty × unit price), not off one unit —
 * that is what `Inv_Receipt_d.Discount_Value` means and what the server
 * subtracts in `computeLines`.
 */
export interface LineDiscount {
  type: DiscountType;
  value: number;
  /** mstr_DiscountReasons row, when the tenant has them. */
  reasonId?: number | null;
  /** The reason's name, for the cart and the receipt. */
  reason: string;
  /** Username of the manager who approved an over-limit line discount. */
  approvedBy?: string;
  /**
   * Security_Users.Id of that manager. An order carries ONE
   * `manager_override_user_id`, so this is folded in beside the header
   * discount's when the request is built — see `orderOverrideUserId`.
   */
  managerOverrideUserId?: number;
}

export interface Discount {
  type: DiscountType;
  value: number;
  /**
   * Free-form short label shown next to the discount in the cart summary
   * and on the receipt. For manual discounts this is the chosen reason's
   * Name (e.g. "Loyalty"). For coupon discounts this is the campaign
   * name from GL_Coupon_h.CouponName (e.g. "Eid Mubarak 2026 promo").
   */
  reason: string;
  /** Username of the manager who approved an over-limit discount, when applicable. */
  approvedBy?: string;
  /**
   * Whether this discount came from a coupon code (vs being entered
   * manually). Drives UX in the modal — when a coupon is applied the
   * manual percent/amount toggle is disabled.
   */
  source: 'manual' | 'coupon';
  /**
   * The coupon code as the cashier typed it (or scanned). Sent on
   * POST /orders as `coupon_code` so the receipt records redemption.
   * Only populated when source === 'coupon'.
   */
  couponCode?: string;
  /**
   * GL_Coupon_d.Id from the validation response. Useful for analytics +
   * a defense-in-depth lookup on the orders endpoint (so it knows the
   * cashier didn't fabricate the code). Only populated when source === 'coupon'.
   */
  couponDId?: number;
  /**
   * True when the NEW coupon engine (migration 038) priced this discount.
   *
   * Such a coupon is sent to the server as a CODE, not as an amount: the server
   * re-validates it, prices it and books the redemption. The value here is only
   * for display and for the cart's expected totals — `usePlaceOrder` keeps it
   * out of `discount_value` so it cannot be counted twice.
   */
  serverPriced?: boolean;
  /**
   * mstr_DiscountReasons.ID of the chosen reason. Sent on POST /orders
   * as `discount_reason_id`. Only populated when source === 'manual'.
   */
  discountReasonId?: number;
  /**
   * Security_Users.Id of the authorizing user when an override was
   * required. Sent on POST /orders as `manager_override_user_id`.
   * Null/undefined when the cashier's own permission was sufficient.
   */
  managerOverrideUserId?: number;
}

/**
 * A loyalty redemption applied to the cart.
 *
 * `value` is the money the points are worth, as priced by the SERVER (via
 * `POST /loyalty/quote`) — never computed in the browser. It behaves like a
 * second header discount: it reduces the pre-tax base, so VAT is charged on the
 * amount actually payable.
 */
export interface LoyaltyRedemption {
  points: number;
  value: number;
  /** Balance before this redemption, for the "x left" hint in the UI. */
  balancePoints: number;
  /**
   * Booked as a "Loyality" (Code -2) PAYMENT, as the legacy till did, instead
   * of a discount: the invoice keeps its full value and tax, and the points
   * come off what is still owed. Set when the tenant has that payment type.
   */
  asPayment?: boolean;
}

export type OrderType = 'walkin' | 'delivery';

/**
 * Phase 9 — top-level order mode for the cashier shell.
 *
 * Distinct from the legacy `orderType` (which stays around for parked-orders
 * compatibility). `orderMode === 'delivery'` is what gates the
 * DeliveryConfirmModal and triggers `order_type: 2` on POST /orders.
 *
 * 'takeaway' is the non-dine-in, non-delivery counter sale (formerly labelled
 * "Sales"/"Walk-in"). It still maps to the legacy `orderType: 'walkin'` tag on
 * the wire — the rename is terminology-only, the numeric order_type is unchanged.
 */
export type OrderMode = 'takeaway' | 'dine-in' | 'delivery' | 'pickup' | 'membership';

/**
 * Per-spec section 5.6. Holds the half-filled delivery form so the cashier
 * can flip between Sales and Delivery without losing their progress. Cleared
 * on order create or explicit cart reset; preserved across mode switches.
 */
export interface DeliveryDraft {
  customerId: number | null;
  addressId: number | null;
  driverId: number | null;
  deliveryFees: number;
  /** ISO datetime; default = now() set on first transition into delivery. */
  deliveryDate: string;
  referenceNo: string;
}

const emptyDeliveryDraft = (): DeliveryDraft => ({
  customerId: null,
  addressId: null,
  driverId: null,
  deliveryFees: 0,
  deliveryDate: new Date().toISOString(),
  referenceNo: '',
});

/**
 * The table attached to the cart (dine-in). Sent to the server as
 * `branch_table_id` so Inv_Receipt_h.BranchTableId is set on save, and
 * also as `table_label` so it appears on the printed receipt / kitchen
 * ticket. `label` is what we display in the cart panel; `tableNumber` is
 * the raw BranchTables.TableNumber used by the receipt template together
 * with the tenant terminology (e.g. "Sun bed: 5").
 */
export interface CartTable {
  id: number;
  label: string;
  tableNumber: number;
}

interface CartState {
  lines: CartLine[];
  customer: Customer | null;
  /**
   * Which of the customer's addresses this order is for
   * (`Inv_CashCustomerDetail.id`), when they have more than one and the
   * cashier chose. Null means "don't say" — the server then behaves exactly
   * as it did before this existed. The DELIVERY workspace keeps its own
   * choice in `delivery.addressId`; this is the one every other order type
   * uses.
   */
  customerAddressId: number | null;
  table: CartTable | null;
  delivery: DeliveryDetails | null;
  discount: Discount | null;
  /** Server-priced loyalty redemption, or null when no points are being spent. */
  loyaltyRedeem: LoyaltyRedemption | null;
  /**
   * The shop's automatic promotions for this basket, priced by the SERVER.
   *
   * Never computed here: the rules, the scoping and the stacking all live on
   * the server, and the till is not trusted with a discount amount. This is a
   * cached copy of the last quote so the cashier can tell the customer what
   * they are saving before taking payment — the sale is priced again on the
   * server when it is charged, and that figure wins.
   */
  promotionQuote: {
    totalDiscount: number;
    /** level 'line' = an item-level promotion, shown in its lines instead of as a row. */
    promotions: Array<{ name: string; nameAr?: string | null; amount: number; level?: 'line' | 'order' }>;
    /**
     * Each SALE line's share of the item-level promotions, in the order the
     * sale lines were quoted (the cart's own order, returns skipped). Already
     * inside totalDiscount — DISPLAY ONLY, never added to anything.
     */
    lineDiscounts?: number[];
  } | null;
  /**
   * True from the moment the basket changes until the server has answered for
   * it — i.e. `promotionQuote` describes an older basket than `lines`.
   *
   * The cart must not be charged in that window. The cashier collects
   * `totals().saleTotal`, which is promotion-inclusive, while the server
   * re-prices the basket for real and rejects a payment sum that does not
   * match it (PAYMENT_SUM_MISMATCH) — so charging against a stale quote fails
   * the sale. It is only the debounce plus one round trip.
   */
  promotionQuotePending: boolean;
  orderType: OrderType;
  orderMode: OrderMode;
  deliveryDraft: DeliveryDraft;
  /** Mock order number — increments on each new sale. */
  orderNumber: number;
  /**
   * Cashier-attached cart-level note (free-form, persists across park/recall).
   * Typed in the payment modal's **Notes** box and printed on the A4 sheet
   * under الملاحظات. Also what the kitchen ticket shows as the order note.
   */
  note: string | null;
  /**
   * The payment modal's **Reference no.** box — a ticket number, a quotation
   * number, whatever the branch writes on the paper. Lands in
   * `Inv_Receipt_h.Memo` and prints under المرجع.
   */
  referenceNo: string | null;
  /**
   * When non-null, the cart is in "edit mode": charging sends PUT /receipts/:id
   * instead of POST /orders. Cleared on reset() or successful charge.
   */
  editingReceiptId: number | null;
  /**
   * True when `editingReceiptId` is a HELD invoice (IsHolded = 1) reopened
   * from the Held Orders screen. Charging then re-holds its lines and pays it
   * (PUT + POST /held-orders/:id/pay) instead of editing a paid receipt, and
   * Hold saves the changes without taking any money.
   */
  editingHeld: boolean;
  /**
   * LAUNDRY Collect: the held laundry order reopened to be paid and handed
   * over. Charge then calls the laundry collect (held-order pay + Delivered,
   * one transaction) with this as the status the laundry board showed. Null
   * on everything else.
   */
  laundryCollectStatus: number | null;
  /**
   * LAUNDRY pay-now: the cashier chose "Take in · pay now" — the next charge is
   * a laundry order (Pickup, this ready-by, `laundry_order`) and prints the
   * laundry ticket. Cleared when the payment window closes.
   */
  laundryPayNow: { readyBy: string } | null;
  setLaundryPayNow: (v: { readyBy: string } | null) => void;
  /**
   * MEMBERSHIP invoice: the From / To dates asked before the payment window
   * (MembershipDatesDialog), sent as `membership` on the order. Cleared when
   * the payment window closes.
   */
  membershipDates: { start: string; end: string } | null;
  setMembershipDates: (v: { start: string; end: string } | null) => void;
  setLaundryCollectStatus: (status: number | null) => void;
  /**
   * The manager's password once approved for THIS held-order edit — sent with
   * the re-hold so the server can check it again. Never persisted; cleared on
   * reset and on every hydrate.
   */
  heldManagerPassword: string | null;
  setHeldManagerPassword: (password: string | null) => void;
  /**
   * "Change Salesman" — who this receipt is credited to (Inv_Receipt_h
   * .Empoyee_ID). Null = the signed-in cashier, which is what legacy resets to
   * after every sale. Set from the receipt when an order is reopened for edit.
   */
  creator: { id: number; name: string | null; nameAr: string | null } | null;
  setCreator: (creator: CartState['creator']) => void;
  /**
   * When non-null, the return lines in this cart came from an existing invoice
   * (archive "Exchange"/"Return"). Sent as `parent_receipt_id` so the server
   * links the new receipts to the original and caps the return quantities.
   * Null for direct negative-qty returns typed on the counter.
   */
  exchangeParentId: number | null;
  /**
   * The source invoice's invoice-level discount as a share of its line value
   * (0..1), set with `exchangeParentId`. The returned lines carry that same
   * share of their value as the RETURN's whole-receipt discount — the item
   * prices stay as sold (the user, 2026-09-28). 0 when not returning from an
   * invoice.
   */
  returnHeaderShare: number;

  // Actions
  /**
   * `combine` = CombineQtyOnItemScan. When true and an identical plain line is
   * already in the cart, its quantity is bumped instead of a second row being
   * added. Callers that are not the scan path leave it unset.
   */
  addLine: (
    product: MockProduct,
    mods: CartLineMod[],
    qty: number,
    note?: string,
    combine?: boolean,
  ) => void;
  /**
   * Add a free membership-redemption line drawing down `membershipDetailId`.
   * unitPrice is forced to 0 (the item is prepaid). qty defaults to 1.
   */
  addMembershipLine: (product: MockProduct, membershipDetailId: number, qty?: number) => void;
  /**
   * Add a gift-card SALE line: one card, at face value, on the tenant's
   * gift-card catalog item. One line per card — quantity is deliberately fixed
   * at 1 because each card needs its own number, PIN and recipient.
   */
  addGiftCardLine: (product: MockProduct, value: number, draft: GiftCardIssueDraft) => void;
  changeQty: (cartId: string, delta: number) => void;
  /** Set a line's quantity to an absolute value (typed directly in the cart). */
  setQty: (cartId: string, qty: number) => void;
  /**
   * Edit a line by its TOTAL price (admin flag IsTotalPriceChanges). Depending
   * on the tenant's WhenChangeTotal setting, the new total is back-solved into
   * either the quantity (mode 'quantity': qty = total / unitPrice) or the unit
   * price (mode 'price': unitPrice = total / qty) — matching the legacy POS.
   */
  setLineTotal: (cartId: string, newTotal: number, mode: 'quantity' | 'price') => void;
  /**
   * Set a line's UNIT price directly — the legacy item dialog's price box,
   * gated on the `Edit Cart Item Price` permission and the cashier's
   * ChangeSalesPriceMode (checked by the caller). 0 is a price; a negative is
   * refused, as for the total.
   */
  setLineUnitPrice: (cartId: string, unitPrice: number) => void;
  /**
   * Replace a line's chosen options (and its note) — the options window
   * reopened from the cart. The unit price moves by the change in the
   * options' price deltas only, so a price the cashier typed by hand is
   * kept rather than reset to the catalog's.
   */
  setLineMods: (cartId: string, mods: CartLineMod[], note?: string) => void;
  /**
   * Re-price every ordinary sale line from a new invoice-source price tier.
   *
   * `basePriceFor` resolves an item id to its price on the new tier and is
   * supplied by the caller, which is what keeps the catalog out of this store:
   * the cart holds flattened `MockProduct`s with a single `price`, while the
   * tiers live on `CatalogItem.units`. Returning null leaves a line alone (the
   * item is no longer in the catalog — better a stale price than a zero one).
   *
   * Modifier deltas are NOT re-tiered: `mstr_ModifierOptions.PriceDelta` has
   * one value per option, with no per-tier variant to read. Each line is
   * rebuilt as newBase + the same modifier sum it already carried, which is
   * also what the legacy POS does.
   *
   * Skipped deliberately: return lines (priced from the invoice being
   * refunded), membership redemptions (always 0), and gift-card sales (the
   * line IS the card's face value).
   */
  repriceLinesForTier: (
    basePriceFor: (productId: number, unitId?: number | null) => number | null,
  ) => void;
  /** Flip a line between sold and returned (direct exchange on the counter). */
  toggleLineReturn: (cartId: string) => void;
  /**
   * Flip a return line between "goes back on the shelf" and "write it off".
   * Only reachable for items flagged `restockOnReturn`; unticking makes the
   * backend write a Code 61 Destruction voucher for the line.
   */
  toggleLineRestock: (cartId: string) => void;
  /** Set (or clear) one of a line's two salesmen. */
  setLineSalesman: (cartId: string, slot: 1 | 2, salesmanId: number | null) => void;
  /** Set (or clear) a line's own discount. Ignored on a return line. */
  setLineDiscount: (cartId: string, discount: LineDiscount | null) => void;
  /**
   * Set (or clear) a line's free-form note — the same "no onions" a QR-menu
   * customer types. It travels to `pos_order_lines.notes`, onto the kitchen
   * ticket and the KDS card, so a whitespace-only note is stored as
   * `undefined` rather than an empty string that would print a blank line.
   */
  setLineNote: (cartId: string, note: string | null) => void;
  removeLine: (cartId: string) => void;
  setCustomer: (c: Customer | null) => void;
  setCustomerAddressId: (id: number | null) => void;
  setTable: (t: CartTable | null) => void;
  setDelivery: (d: DeliveryDetails | null) => void;
  setDiscount: (d: Discount | null) => void;
  /** Apply (or clear) a server-priced loyalty redemption. */
  setLoyaltyRedeem: (r: LoyaltyRedemption | null) => void;
  /** Cache the latest server promotion quote (or clear it). */
  setPromotionQuote: (q: CartState["promotionQuote"]) => void;
  /** Mark the cached quote as out of date (or caught up) with the basket. */
  setPromotionQuotePending: (pending: boolean) => void;
  setOrderType: (t: OrderType) => void;
  setOrderMode: (m: OrderMode) => void;
  setDeliveryDraft: (patch: Partial<DeliveryDraft>) => void;
  clearDeliveryDraft: () => void;
  setNote: (note: string | null) => void;
  setReferenceNo: (ref: string | null) => void;
  setEditingReceiptId: (id: number | null) => void;
  reset: () => void;
  hydrateFromParked: (snap: ParkedOrderSnapshot) => void;
  hydrateFromReceipt: (detail: ReceiptDetail) => void;
  /**
   * Load an archived receipt's lines as RETURN lines (negative) to start an
   * exchange/return against it. Sets exchangeParentId; the cashier then adds
   * new items (sale lines) for an exchange, or charges as-is for a refund.
   */
  hydrateForExchange: (detail: ReceiptDetail) => void;

  // Selectors (computed)
  totals: () => {
    subtotal: number;
    /**
     * Money taken off individual lines. Already subtracted from `subtotal`,
     * because a line discount lives inside the line's own total — it is
     * reported here only so the cart can show what was given.
     */
    lineDiscountTotal: number;
    discount: number;
    /** Money value of redeemed points — a second discount, shown separately. */
    loyaltyDiscount: number;
    /** Points booked as a "Loyality" payment (`asPayment`) — already off `saleTotal`, not a discount. */
    loyaltyPayment: number;
    /** The sale leg's invoice total before points paid as a payment. */
    saleGross: number;
    /** Money the shop's automatic promotions took off, priced by the server. */
    promotionDiscount: number;
    /**
     * The ORDER-level promotions that applied (basket / free delivery), named.
     * Item-level ones are shown in their lines (promotionByLine) instead.
     */
    promotions: Array<{ name: string; nameAr?: string | null; amount: number; level?: 'line' | 'order' }>;
    /**
     * cartId → that line's share of the item-level promotions. DISPLAY ONLY:
     * the money is already inside promotionDiscount.
     */
    promotionByLine: Record<string, number>;
    /** Σ promotionByLine — the part of promotionDiscount shown in the lines. */
    linePromotionTotal: number;
    deliveryFee: number;
    afterDiscount: number;
    vat: number;
    total: number;
    itemCount: number;
    // Exchange/return breakdown. For a plain sale: returnSubtotal/returnVat/
    // returnTotal = 0, net = total, mode = 'sale'.
    returnSubtotal: number;
    /**
     * The return's whole-receipt discount: the source invoice's invoice-level
     * share of the returned value (`returnInheritedDiscount`) plus, on a PURE
     * return, the cashier's own discount (`discount`). Taken off the refund.
     */
    returnDiscount: number;
    /** Of `returnDiscount`, the part inherited from the source invoice — sent as `return_discount_value`. */
    returnInheritedDiscount: number;
    returnVat: number;
    returnTotal: number;
    /** Sale-leg grand total (subtotal − discount + delivery + saleVat). */
    saleTotal: number;
    /** Net physically settled = saleTotal − returnTotal (signed). */
    net: number;
    mode: 'sale' | 'return' | 'exchange';
    hasReturns: boolean;
  };
}

let nextCartId = 1;
const newCartId = () => `c-${Date.now()}-${nextCartId++}`;

/**
 * The smallest quantity a line may hold. Inv_Items.AllowDecimals marks the
 * items sold by weight / fraction (0.5 kg, 0.25 m): those go down to 0.001,
 * the DECIMAL(18,3) resolution of Inv_Receipt_d.Qty. Everything else is whole
 * units with a floor of 1. Shared by the cart stepper, the typed quantity
 * field and the barcode scanner so all three agree.
 */
export function minQtyFor(product: Pick<MockProduct, 'allowDecimals'>): number {
  return product.allowDecimals ? 0.001 : 1;
}

/**
 * Round money at the TENANT's currency precision, mirroring the server's
 * `roundMoney` (orders.service.ts) and the order-request builder
 * (usePlaceOrder). Keeping one rounding rule across display, payment prefill,
 * and the wire is what stops drift between the cart total and the server's
 * computed net value.
 *
 * The precision comes from the print config's base currency (3 for KWD, 2 for
 * EGP/SAR/AED). Reading it per call rather than capturing it once means the
 * cart picks up the real precision as soon as the print config lands, without
 * the cart needing to be a React component.
 */
const rm = (n: number): number => roundMoney(n, moneyPrecision());

export const useCart = create<CartState>((set, get) => ({
  lines: [],
  customer: null,
  customerAddressId: null,
  table: null,
  delivery: null,
  discount: null,
  loyaltyRedeem: null,
  promotionQuote: null,
  promotionQuotePending: false,
  orderType: 'walkin',
  orderMode: 'takeaway',
  deliveryDraft: emptyDeliveryDraft(),
  orderNumber: 4521,
  note: null,
  referenceNo: null,
  editingReceiptId: null,
  editingHeld: false,
  laundryCollectStatus: null,
  setLaundryCollectStatus: (status) => set({ laundryCollectStatus: status }),
  laundryPayNow: null,
  setLaundryPayNow: (v) => set({ laundryPayNow: v }),
  membershipDates: null,
  setMembershipDates: (v) => set({ membershipDates: v }),
  heldManagerPassword: null,
  setHeldManagerPassword: (heldManagerPassword) => set({ heldManagerPassword }),
  creator: null,
  setCreator: (creator) => set({ creator }),
  exchangeParentId: null,
  returnHeaderShare: 0,

  addLine: (product, mods, qty, note, combine) => {
    const modSum = mods.reduce((a, m) => a + m.price, 0);
    const unitPrice = product.price + modSum;

    // CombineQtyOnItemScan — scanning the same item again bumps the line the
    // cashier already has instead of stacking identical rows. Only an
    // unambiguously identical line qualifies: same product, same price, no
    // modifiers or note on either side, and none of the special line kinds
    // (return / membership / gift card) whose quantity means something else.
    if (combine) {
      const twin = [...get().lines].reverse().find(
        (l) =>
          l.product.id === product.id &&
          // A carton line never absorbs a can scan of the same item.
          (l.product.unitId ?? null) === (product.unitId ?? null) &&
          l.unitPrice === unitPrice &&
          l.mods.length === 0 &&
          mods.length === 0 &&
          !l.note &&
          !note &&
          !l.isReturn &&
          l.membershipDetailId == null &&
          l.giftCardIssue == null,
      );
      if (twin) {
        set((s) => ({
          lines: s.lines.map((l) => (l.cartId === twin.cartId ? { ...l, qty: l.qty + qty } : l)),
        }));
        return;
      }
    }

    set((s) => ({
      lines: [
        ...s.lines,
        { cartId: newCartId(), product, mods, qty, unitPrice, note },
      ],
    }));
  },

  addMembershipLine: (product, membershipDetailId, qty = 1) => {
    set((s) => ({
      lines: [
        ...s.lines,
        { cartId: newCartId(), product, mods: [], qty, unitPrice: 0, membershipDetailId },
      ],
    }));
  },

  addGiftCardLine: (product, value, draft) => {
    set((s) => ({
      lines: [
        ...s.lines,
        {
          cartId: newCartId(),
          // The line carries the card's face value as its price, so the sale
          // leg, the receipt and the issued card can never disagree about what
          // the customer paid for.
          product: { ...product, price: value },
          mods: [],
          qty: 1,
          unitPrice: value,
          giftCardIssue: draft,
        },
      ],
    }));
  },

  changeQty: (cartId, delta) => {
    set((s) => ({
      lines: s.lines
        .map((l) => {
          if (l.cartId !== cartId) return l;
          // The +/- buttons step by whole units. A whole-unit item floors at
          // 1. A by-weight item (Inv_Items.AllowDecimals) already below 1
          // (0.5 kg) is left where it is rather than dropped to 0.001 —
          // the cashier types fractions, the buttons never produce them.
          const floor = l.product.allowDecimals ? Math.min(1, l.qty) : 1;
          const next = Math.max(floor, Math.round((l.qty + delta) * 1000) / 1000);
          return { ...l, qty: next };
        })
        .filter((l) => l.qty > 0),
    }));
  },

  setQty: (cartId, qty) => {
    if (!Number.isFinite(qty)) return;
    set((s) => ({
      lines: s.lines.map((l) => {
        if (l.cartId !== cartId) return l;
        // qty carries 3 decimals (legacy parity) and sits on the item's own
        // floor: 0.001 when Inv_Items.AllowDecimals is set (sold by weight /
        // fraction), else 1 like changeQty.
        const next = Math.max(minQtyFor(l.product), Math.round(qty * 1000) / 1000);
        return { ...l, qty: next };
      }),
    }));
  },

  /**
   * ZERO is a price, not a refusal. A line given away — a comp, a staff meal,
   * a replacement for a spoiled dish — is priced 0 and the order is saved with
   * it, even when that takes the whole order to 0. The old `<= 0` guard
   * silently put the catalog price back, which at the till reads as the box
   * refusing to take the number rather than as a rule.
   *
   * A NEGATIVE total is still refused: the return direction is carried by the
   * line's `isReturn` flag and by the sign of `qty` on the wire, never by a
   * negative price.
   */
  setLineTotal: (cartId, newTotal, mode) => {
    if (!Number.isFinite(newTotal) || newTotal < 0) return;
    set((s) => ({
      lines: s.lines.map((l) => {
        if (l.cartId !== cartId) return l;
        if (mode === 'quantity') {
          // qty = total / unitPrice — qty carries 3 decimals (legacy parity).
          // Zero stays refused HERE: in this mode the price never moves, so a
          // zero total means a zero quantity, and a line of nothing is not a
          // line (the API refuses `qty: 0` outright). Emptying a line is what
          // the remove button is for.
          if (l.unitPrice <= 0) return l;
          const qty = Math.round((newTotal / l.unitPrice) * 1000) / 1000;
          return qty > 0 ? { ...l, qty } : l;
        }
        // mode === 'price': unitPrice = total / qty — money keeps 4 decimals.
        if (l.qty <= 0) return l;
        const unitPrice = Math.round((newTotal / l.qty) * 10000) / 10000;
        return { ...l, unitPrice, priceOverrideFrom: l.priceOverrideFrom ?? l.unitPrice };
      }),
    }));
  },

  setLineUnitPrice: (cartId, unitPrice) => {
    if (!Number.isFinite(unitPrice) || unitPrice < 0) return;
    const rounded = Math.round(unitPrice * 10000) / 10000;
    set((s) => ({
      lines: s.lines.map((l) =>
        l.cartId === cartId ? { ...l, unitPrice: rounded, priceOverrideFrom: l.priceOverrideFrom ?? l.unitPrice } : l,
      ),
    }));
  },

  setLineMods: (cartId, mods, note) => {
    set((s) => ({
      lines: s.lines.map((l) => {
        if (l.cartId !== cartId) return l;
        const oldSum = l.mods.reduce((a, m) => a + m.price, 0);
        const newSum = mods.reduce((a, m) => a + m.price, 0);
        const unitPrice = Math.round((l.unitPrice - oldSum + newSum) * 10000) / 10000;
        const trimmed = note?.trim();
        return {
          ...l,
          mods,
          unitPrice: Math.max(0, unitPrice),
          note: trimmed ? trimmed : undefined,
        };
      }),
    }));
  },

  repriceLinesForTier: (basePriceFor) => {
    set((s) => ({
      lines: s.lines.map((l) => {
        if (l.isReturn) return l;
        if (l.membershipDetailId != null) return l;
        if (l.giftCardIssue) return l;

        const base = basePriceFor(l.product.id, l.product.unitId ?? null);
        if (base == null || !Number.isFinite(base) || base <= 0) return l;

        const modSum = l.mods.reduce((sum, m) => sum + m.price, 0);
        const unitPrice = roundMoney(base + modSum, moneyPrecision());
        if (unitPrice === l.unitPrice) return l;

        // `product.price` is kept in step so anything that re-derives from the
        // product later (the modifier modal reopening a line, a park/recall
        // round-trip) agrees with the line it came from.
        return { ...l, product: { ...l.product, price: base }, unitPrice };
      }),
    }));
  },

  toggleLineReturn: (cartId) => {
    set((s) => ({
      lines: s.lines.map((l) =>
        // Flipping back to SOLD drops any restock decision — it only means
        // something on a return, and a stale `false` here would write off
        // stock if the line were flipped to a return again later.
        l.cartId === cartId
          ? {
              ...l,
              isReturn: !l.isReturn,
              restock: l.isReturn ? undefined : useTenantConfig.getState().restockOnReturnDefault,
            }
          : l,
      ),
    }));
  },

  toggleLineRestock: (cartId) => {
    set((s) => ({
      lines: s.lines.map((l) =>
        l.cartId === cartId ? { ...l, restock: !(l.restock ?? true) } : l,
      ),
    }));
  },

  setLineNote: (cartId, note) => {
    const clean = (note ?? '').trim();
    set((s) => ({
      lines: s.lines.map((l) =>
        l.cartId === cartId ? { ...l, note: clean === '' ? undefined : clean } : l,
      ),
    }));
  },

  setLineDiscount: (cartId, discount) => {
    set((s) => ({
      lines: s.lines.map((l) =>
        l.cartId !== cartId ? l : { ...l, lineDiscount: discount },
      ),
    }));
  },

  setLineSalesman: (cartId, slot, salesmanId) => {
    set((s) => ({
      lines: s.lines.map((l) => {
        if (l.cartId !== cartId) return l;
        if (slot === 1) {
          // Clearing salesman 1 also clears salesman 2 (can't have #2 without #1).
          return salesmanId == null
            ? { ...l, salesman1Id: null, salesman2Id: null }
            : { ...l, salesman1Id: salesmanId };
        }
        return { ...l, salesman2Id: salesmanId };
      }),
    }));
  },

  removeLine: (cartId) => {
    set((s) => ({ lines: s.lines.filter((l) => l.cartId !== cartId) }));
  },

  // Changing the customer always drops any redemption: the points belonged to
  // whoever was attached when the quote was taken, and silently spending the
  // NEXT customer's balance would be worse than making the cashier re-apply.
  // The address belongs to the customer, so changing the customer drops it
  // for the same reason the redemption goes.
  setCustomer: (customer) =>
    set({ customer, customerAddressId: null, loyaltyRedeem: null }),
  setCustomerAddressId: (customerAddressId) => set({ customerAddressId }),
  setTable: (table) => set({ table }),
  setDelivery: (delivery) => set({ delivery }),
  setDiscount: (discount) => set({ discount }),
  setLoyaltyRedeem: (loyaltyRedeem) => set({ loyaltyRedeem }),
  setPromotionQuote: (promotionQuote) => set({ promotionQuote, promotionQuotePending: false }),
  // Same value in = same state object out, so zustand skips the notify and a
  // re-render can never bounce back into the effect that set this.
  setPromotionQuotePending: (pending) =>
    set((s) => (s.promotionQuotePending === pending ? s : { promotionQuotePending: pending })),
  setOrderType: (orderType) => set({ orderType }),
  setEditingReceiptId: (editingReceiptId) => set({ editingReceiptId }),
  setOrderMode: (orderMode) => {
    // Per spec 5.1: switching modes preserves the cart AND the delivery
    // draft (so flipping Delivery → Sales → Delivery doesn't lose what was
    // typed). We also keep `orderType` in sync for legacy callers that
    // still read it (PaymentModal, parked-orders snapshot).
    set({
      orderMode,
      orderType: orderMode === 'delivery' ? 'delivery' : 'walkin',
    });
  },
  setDeliveryDraft: (patch) =>
    set((s) => ({ deliveryDraft: { ...s.deliveryDraft, ...patch } })),
  clearDeliveryDraft: () => set({ deliveryDraft: emptyDeliveryDraft() }),
  setNote: (note) => set({ note }),
  setReferenceNo: (referenceNo) => set({ referenceNo }),

  reset: () => {
    // Whatever edit was in progress is over, so the price-list chip is
    // selectable again. Safe to call when nothing was locked.
    useInvoiceSources.getState().setLocked(false);
    set((s) => ({
      lines: [],
      customer: null,
      customerAddressId: null,
      table: null,
      delivery: null,
      discount: null,
      loyaltyRedeem: null,
      // The next basket is not this one's. Cleared here rather than waiting for
      // the quote effect to notice, so an emptied cart never shows the old
      // customer's promotion for a frame.
      promotionQuote: null,
      promotionQuotePending: false,
      note: null,
      referenceNo: null,
      editingReceiptId: null,
      editingHeld: false,
      laundryCollectStatus: null,
      laundryPayNow: null,
      membershipDates: null,
      heldManagerPassword: null,
      // Back to the signed-in cashier after every sale, as legacy's ClearCart.
      creator: null,
      exchangeParentId: null,
      returnHeaderShare: 0,
      orderNumber: s.orderNumber + 1,
      // Clear the delivery draft on hard reset (charge/discard); mode flips
      // alone do NOT call reset(), so the draft survives mode switches.
      deliveryDraft: emptyDeliveryDraft(),
    }));
  },

  hydrateFromParked: (snap) => {
    const lines: CartLine[] = snap.lines.map((line) => {
      // `idx` is a position inside the group's option list, not something the
      // snapshot carries — regenerate it here exactly as hydrateFromReceipt
      // does. Legacy snapshots parked before modifiers were carried have no
      // `modifiers` at all and rehydrate as a plain line, as before.
      const mods: CartLineMod[] = (line.modifiers ?? []).map((m, idx) => ({
        option_id: m.option_id,
        group_id: m.group_id,
        group: m.group,
        idx,
        name: m.name,
        nameAr: m.name_ar,
        price: m.price,
        ...(m.qty != null ? { qty: m.qty } : {}),
      }));
      // The parked `unit_price` is item + modifiers (that is what the cart
      // stores). `product.price` is the BASE, so back the modifier sum out —
      // otherwise a tier reprice or reopening the modifier modal would add
      // their money a second time.
      const modSum = mods.reduce((s, m) => s + m.price, 0);
      const product: MockProduct = {
        id: line.product_id,
        name: line.product_name,
        nameAr: line.product_name_ar,
        price: roundMoney(line.unit_price - modSum, moneyPrecision()),
        cat: 'live',
        emoji: '',
        bg: 'var(--surface-2, #f0f4f8)',
        station: '',
        // Recalled in the unit it was parked in; names come back only when the
        // snapshot carried them (a multi-unit item).
        unitId: line.unit_id ?? null,
        ...(line.unit_name || line.unit_name_ar
          ? { unitName: line.unit_name ?? null, unitNameAr: line.unit_name_ar ?? null }
          : {}),
      };
      return {
        cartId: newCartId(),
        product,
        mods,
        qty: line.qty,
        unitPrice: line.unit_price,
        note: line.note ?? undefined,
      };
    });

    const customer: Customer | null = snap.customer
      ? {
          id: snap.customer.customer_id,
          type: snap.customer.type,
          code: null,
          name: snap.customer.name,
          name_ar: snap.customer.name_ar,
          phone: snap.customer.phone,
          email: null,
          address: null,
          category_id: null,
          tax_reg_no: null,
          date_of_birth: null,
          gender_id: null,
          loyalty_balance: null,
          tier: 'new',
          stats: null,
          addresses: null,
        }
      : null;

    const delivery: DeliveryDetails | null = snap.delivery
      ? {
          zone: snap.delivery.zone ?? '',
          fee: snap.delivery.fee,
          eta: 0,
          address: snap.delivery.address ?? '',
          phone: customer?.phone ?? '',
        }
      : null;

    // Recalled discount loses its reason/coupon metadata since the
    // snapshot doesn't carry it (Phase 6 ParkedOrderSnapshot.discount
    // is just type+value). The cashier may need to reapply via the
    // Discount modal; for now we surface it as a 'manual' discount
    // labelled "Recalled" so the totals are right and the cashier sees
    // it came from the parked order.
    const discount: Discount | null = snap.discount
      ? {
          type: snap.discount.type,
          value: snap.discount.value,
          reason: 'Recalled',
          source: 'manual',
        }
      : null;

    set({
      lines,
      customer,
      // A hydrated cart never carries a redemption: any quote was taken
      // against a basket that no longer exists.
      loyaltyRedeem: null,
      // Parked snapshots predate the table feature; recalled carts start
      // without a table attached. The cashier can re-attach via the picker
      // before charging if the parked order belongs to a specific table.
      table: null,
      delivery,
      discount,
      orderType: snap.order_type === 'delivery' ? 'delivery' : 'walkin',
      orderMode:
        snap.order_type === 'delivery'
          ? 'delivery'
          : snap.order_type === 'pickup'
            ? 'pickup'
            : 'takeaway',
      note: snap.note ?? null,
      referenceNo: snap.reference_no ?? null,
      // Phase 9 — rehydrate the delivery draft if the parked order carried
      // one (delivery orders parked mid-form). Falls back to empty for
      // legacy snapshots without `delivery_draft`.
      deliveryDraft: snap.delivery_draft
        ? {
            customerId: snap.delivery_draft.customer_id,
            addressId: snap.delivery_draft.address_id,
            driverId: snap.delivery_draft.driver_id,
            deliveryFees: snap.delivery_draft.delivery_fees,
            deliveryDate: snap.delivery_draft.delivery_date,
            referenceNo: snap.delivery_draft.reference_no,
          }
        : emptyDeliveryDraft(),
    });
  },

  hydrateFromReceipt: (detail) => {
    // Put the price-list chip back on the list this order was rung up on, and
    // lock it. The lines below carry the prices they were SOLD at; letting a
    // cashier re-tier them mid-edit would restate a receipt the customer is
    // already holding. The legacy POS does the same two steps in the same
    // order (order_functions_definitions.js:2223-2238).
    //
    // A receipt with no source_id (taken before the tenant configured price
    // lists, or on a tenant without the column) leaves the chip where it is
    // and still locks it, because whatever is selected did not price these
    // lines either.
    useInvoiceSources.getState().forceActive(detail.source_id ?? null);
    useInvoiceSources.getState().setLocked(true);

    const lines: CartLine[] = detail.lines
      .filter((line) => !line.is_return)
      .map((line) => {
        const mods: CartLineMod[] = line.modifiers.map((m, idx) => ({
          option_id: m.option_id,
          group_id: m.group_id,
          group: m.group_name ?? '',
          idx,
          name: m.option_name,
          nameAr: m.option_name_ar ?? m.option_name,
          price: m.price_delta,
          ...(m.quantity != null ? { qty: m.quantity } : {}),
        }));
        const modSum = mods.reduce((s, m) => s + m.price, 0);
        const basePrice = line.unit_price - modSum;
        // The SAME product a new sale's line carries (the user, 2026-10-01: the
        // edited cart must be exactly the new-invoice cart): barcode and photo
        // from the catalog the till holds, the code, and the item's own
        // decimal-quantity and restock flags. The PRICE stays as sold.
        const extras = catalogProductExtras(line.item_id, line.unit_id ?? null);
        const product: MockProduct = {
          id: line.item_id,
          name: line.item_name,
          nameAr: line.item_name_ar ?? line.item_name,
          price: basePrice,
          cat: 'live',
          emoji: '',
          bg: 'var(--surface-2, #f0f4f8)',
          station: '',
          code: line.item_code ?? extras.code ?? null,
          barcode: extras.barcode ?? null,
          image: extras.image ?? null,
          allowDecimals: line.allow_decimals ?? extras.allowDecimals,
          restockOnReturn: line.restock_on_return ?? extras.restockOnReturn,
          // The unit the line was sold in, so the re-written line converts
          // stock by the same factor; named only on a multi-unit item.
          unitId: line.unit_id ?? null,
          ...(line.has_multiple_units
            ? { unitName: line.unit_name ?? null, unitNameAr: line.unit_name_ar ?? null }
            : {}),
        };
        // The line's OWN discount (Inv_Receipt_d.Discount_Value — the line's
        // total — and Discount_Percent). Dropping it showed the edited cart at
        // the undiscounted price, and saving the edit then raised the invoice
        // by exactly that discount (Ponytail_test 88167, 2026-10-01). A percent
        // reloads as a percent; anything else as the money it took off.
        const lineDiscount: LineDiscount | null =
          Number(line.discount_percent) > 0
            ? { type: 'percent', value: Number(line.discount_percent), reason: '' }
            : Number(line.discount_value) > 0
              ? { type: 'amount', value: Number(line.discount_value), reason: '' }
              : null;
        return {
          cartId: newCartId(),
          product,
          mods,
          qty: line.qty,
          unitPrice: line.unit_price,
          ...(lineDiscount ? { lineDiscount } : {}),
          note: line.notes ?? undefined,
          // Who sold it, so an edit that changes something else does not
          // silently drop the salesman off the line.
          ...(line.salesman_id != null ? { salesman1Id: line.salesman_id } : {}),
          ...(line.salesman2_id != null ? { salesman2Id: line.salesman2_id } : {}),
          ...(detail.is_holded ? { heldBaseQty: line.qty } : {}),
        };
      });

    const customer: Customer | null = detail.customer_id
      ? {
          id: detail.customer_id,
          type: 'cash',
          code: null,
          name: detail.customer_name,
          name_ar: detail.customer_name_ar,
          phone: detail.customer_phone,
          email: null,
          address: null,
          category_id: null,
          tax_reg_no: null,
          date_of_birth: null,
          gender_id: null,
          loyalty_balance: null,
          tier: 'new',
          stats: null,
          addresses: null,
        }
      : null;

    const discount: Discount | null =
      detail.discount_percent > 0
        ? { type: 'percent', value: detail.discount_percent, reason: 'Recalled', source: 'manual' }
        : detail.discount_value > 0
        ? { type: 'amount', value: detail.discount_value, reason: 'Recalled', source: 'manual' }
        : null;

    const isDelivery = detail.order_type === 2;
    // The table the receipt sits on comes back with it — a held dine-in order
    // reopened for more items must stay on its table (it lost it before, and
    // the table then looked free to the next order).
    const tableNo = detail.table_number != null ? Number(detail.table_number) : NaN;
    const table: CartTable | null =
      !isDelivery && detail.branch_table_id != null
        ? {
            id: detail.branch_table_id,
            label:
              detail.table_name?.trim() ||
              (Number.isFinite(tableNo) ? `Table ${tableNo}` : `Table #${detail.branch_table_id}`),
            tableNumber: Number.isFinite(tableNo) ? tableNo : 0,
          }
        : null;
    const orderMode: OrderMode = isDelivery ? 'delivery' : table ? 'dine-in' : 'takeaway';
    const orderType: OrderType = isDelivery ? 'delivery' : 'walkin';

    set({
      lines,
      customer,
      // A hydrated cart never carries a redemption: any quote was taken
      // against a basket that no longer exists.
      loyaltyRedeem: null,
      table,
      discount,
      delivery: null,
      note: detail.notes ?? null,
      referenceNo: detail.memo ?? null,
      orderMode,
      orderType,
      editingReceiptId: detail.id,
      editingHeld: detail.is_holded === true,
      laundryCollectStatus: null,
      laundryPayNow: null,
      membershipDates: null,
      heldManagerPassword: null,
      // The button shows who the receipt is credited to, as legacy's edit did.
      creator:
        detail.empoyee_id != null
          ? { id: detail.empoyee_id, name: detail.empoyee_name ?? null, nameAr: detail.empoyee_name_ar ?? null }
          : null,
      // See the invoice-source lock below: `editingReceiptId` is what drives it.
      deliveryDraft: isDelivery
        ? {
            customerId: detail.customer_id,
            addressId: null,
            driverId: null,
            deliveryFees: detail.delivery_fees,
            deliveryDate: detail.entry_date,
            referenceNo: detail.reference_no ?? '',
          }
        : emptyDeliveryDraft(),
    });
  },

  hydrateForExchange: (detail) => {
    // An exchange creates a NEW receipt pair, so the price list stays live:
    // the return lines are refunded at what they were sold for (already in
    // `unit_price`), while any sale lines the cashier adds are priced on
    // whatever list the till is on now.
    useInvoiceSources.getState().setLocked(false);

    // Load every line of the source invoice as a RETURN line. The cashier can
    // trim quantities / remove lines (partial return) and add new sale lines
    // (exchange) before charging.
    const restockDefault = useTenantConfig.getState().restockOnReturnDefault;

    // The return mirrors the invoice (the user, 2026-09-28): each item keeps
    // its price as sold and carries its OWN line discount again, and the
    // invoice-level discount comes back as the return's whole-receipt
    // discount, in the same proportion of line value it had on the invoice.
    // The share is taken against the sum of the line totals, not the header
    // Value, which older ERP invoices do not always keep equal to it — the
    // same basis the server's `loadParentPaidPerUnit` uses.
    const lineSum = detail.lines.reduce((s, l) => s + Math.abs(l.line_total), 0);
    // Points paid as a "Loyality" (-2) payment are refunded as points are on
    // a discount invoice — not in money — so they count with the discount, at
    // their PRE-TAX share (they paid an amount that includes tax).
    const pointsGross = (detail.payments ?? [])
      .filter((p) => p.payment_type_code === -2)
      .reduce((s, p) => s + Math.abs(p.amount), 0);
    const pointsPaid = pointsGross > 0 && detail.net_value > 0
      ? (pointsGross * (detail.value - (detail.discount_value ?? 0) + (detail.delivery_fees ?? 0))) / detail.net_value
      : 0;
    const share = lineSum > 0
      ? Math.min(Math.max(((detail.discount_value ?? 0) + pointsPaid) / lineSum, 0), 1)
      : 0;

    const lines: CartLine[] = detail.lines.map((line) => {
      const mods: CartLineMod[] = line.modifiers.map((m, idx) => ({
        option_id: m.option_id,
        group_id: m.group_id,
        group: m.group_name ?? '',
        idx,
        name: m.option_name,
        nameAr: m.option_name_ar ?? m.option_name,
        price: m.price_delta,
        ...(m.quantity != null ? { qty: m.quantity } : {}),
      }));
      const modSum = mods.reduce((s, m) => s + m.price, 0);
      const basePrice = line.unit_price - modSum;
      const product: MockProduct = {
        id: line.item_id,
        name: line.item_name,
        nameAr: line.item_name_ar ?? line.item_name,
        price: basePrice,
        cat: 'live',
        emoji: '',
        bg: 'var(--surface-2, #f0f4f8)',
        station: '',
        // The item number, barcode and photo, as a new sale's line carries them.
        code: line.item_code ?? catalogProductExtras(line.item_id, line.unit_id ?? null).code ?? null,
        barcode: catalogProductExtras(line.item_id, line.unit_id ?? null).barcode ?? null,
        image: catalogProductExtras(line.item_id, line.unit_id ?? null).image ?? null,
        restockOnReturn: line.restock_on_return,
        allowDecimals: line.allow_decimals,
        // Returned in the unit it was sold in: a carton comes back as a
        // carton, and the stock write-back converts by the carton's factor.
        unitId: line.unit_id ?? null,
        ...(line.has_multiple_units
          ? { unitName: line.unit_name ?? null, unitNameAr: line.unit_name_ar ?? null }
          : {}),
      };
      // The original line's own discount, per unit (price × qty − its total).
      const soldQty = Math.abs(line.qty);
      const refundUnitDiscount = soldQty > 0
        ? Math.max(0, line.unit_price - Math.abs(line.line_total) / soldQty)
        : 0;
      return {
        cartId: newCartId(),
        product,
        mods,
        qty: Math.abs(line.qty),
        unitPrice: line.unit_price,
        ...(refundUnitDiscount > 0 ? { refundUnitDiscount } : {}),
        note: line.notes ?? undefined,
        isReturn: true,
        // The tenant's default, same as a counter return. Only shown for
        // items flagged restockOnReturn.
        restock: restockDefault,
      };
    });

    const customer: Customer | null = detail.customer_id
      ? {
          id: detail.customer_id,
          type: 'cash',
          code: null,
          name: detail.customer_name,
          name_ar: detail.customer_name_ar,
          phone: detail.customer_phone,
          email: null,
          address: null,
          category_id: null,
          tax_reg_no: null,
          date_of_birth: null,
          gender_id: null,
          loyalty_balance: null,
          tier: 'new',
          stats: null,
          addresses: null,
        }
      : null;

    set({
      lines,
      customer,
      // A hydrated cart never carries a redemption: any quote was taken
      // against a basket that no longer exists.
      loyaltyRedeem: null,
      table: null,
      // The invoice's discounts come back on their own (each line's own
      // discount on the line, the invoice-level share via returnHeaderShare);
      // this is the cashier's discount, which starts empty.
      discount: null,
      delivery: null,
      note: null,
      orderMode: 'takeaway',
      orderType: 'walkin',
      // A NEW receipt pair is created (not an edit of the original).
      editingReceiptId: null,
      editingHeld: false,
      laundryCollectStatus: null,
      laundryPayNow: null,
      membershipDates: null,
      heldManagerPassword: null,
      creator: null,
      exchangeParentId: detail.id,
      returnHeaderShare: share,
      deliveryDraft: emptyDeliveryDraft(),
    });
  },

  totals: () => {
    const lines = get().lines;
    const saleLines = lines.filter((l) => !l.isReturn);
    const returnLines = lines.filter((l) => l.isReturn);

    // Round each line total BEFORE summing — this must mirror the server
    // (orders.service `computeLegTotals`: `roundMoney(qty*price)` per line, then
    // `roundMoney(Σ)`) and the wire builder (usePlaceOrder). Summing raw floats
    // and rounding only at the end diverges: e.g. lines 0.454 + 0.134 sum to
    // 0.588 → 0.59, while the server's round-then-sum gives 0.45 + 0.13 = 0.58.
    // That one-cent gap made the PaymentModal (which prefills from t.saleTotal)
    // collect 0.59 against a server net of 0.58 → PAYMENT_SUM_MISMATCH, and it
    // also showed a Subtotal/Total that didn't match the receipt's line items.
    //
    // Each line is priced NET of its own discount (`lineNetTotal`), because
    // that is what the server sums into `LegTotals.value` — a line discount
    // never appears in the header discount figure. The header discount below
    // is therefore taken from an already-discounted subtotal, in that order,
    // exactly as the server does it.
    const subtotal = rm(saleLines.reduce((a, l) => a + lineNetTotal(l, rm), 0));
    // Return lines are refunded NET of their discount too (what the invoice
    // was actually paid, less anything taken off by hand) — the server's
    // return-leg `value`.
    const returnSubtotal = rm(returnLines.reduce((a, l) => a + lineNetTotal(l, rm), 0));
    const lineDiscountTotal = rm(saleLines.reduce((a, l) => a + lineDiscountAmount(l, rm), 0));

    // A PURE return takes the cashier's invoice-level discount off the REFUND
    // (the user, 2026-09-28); in a sale or an exchange it stays on the sale.
    // Same split as the server's `computeExchange`.
    const pureReturn = saleLines.length === 0 && returnLines.length > 0;
    // Returning from an invoice: its invoice-level discount comes back as the
    // RETURN's whole-receipt discount, the same share of the returned value
    // (`returnInheritedDiscount`, sent as `return_discount_value`). 0 for a
    // counter return typed without an invoice.
    const share = get().exchangeParentId != null ? get().returnHeaderShare : 0;
    const returnInheritedDiscount = share > 0 ? rm(returnSubtotal * share) : 0;
    const discountBase = pureReturn ? rm(returnSubtotal - returnInheritedDiscount) : subtotal;
    const discount = get().discount;
    let discountAmount = 0;
    if (discount) {
      discountAmount =
        discount.type === 'percent' ? rm(discountBase * (discount.value / 100)) : rm(discount.value);
      discountAmount = Math.min(discountAmount, discountBase);
    }
    const saleDiscount = pureReturn ? 0 : discountAmount;
    const returnDiscount = rm(returnInheritedDiscount + (pureReturn ? discountAmount : 0));
    // Redeemed points behave as a second header discount and are capped at
    // what is left after the cashier's own discount, mirroring the server
    // (`computeLegTotals`: discountValue = cashier discount + loyalty value).
    const redeem = get().loyaltyRedeem;
    const loyaltyDiscount = redeem && !redeem.asPayment
      ? Math.min(rm(redeem.value), rm(subtotal - saleDiscount))
      : 0;
    // The shop's automatic promotions, as the server last priced them. Capped
    // at what is left, mirroring the server's own basket floor, so a stale
    // quote can never drive the cart negative between refreshes.
    const quote = get().promotionQuote;
    const promotionDiscount = quote
      ? Math.min(rm(quote.totalDiscount), rm(subtotal - saleDiscount - loyaltyDiscount))
      : 0;
    const afterDiscount = rm(subtotal - saleDiscount - loyaltyDiscount - promotionDiscount);
    // Delivery fee source:
    //   - delivery mode: deliveryDraft.deliveryFees (lives on the form)
    //   - non-delivery: legacy `delivery: DeliveryDetails` fee (prototype)
    const state = get();
    const deliveryFee = rm(
      state.orderMode === 'delivery'
        ? state.deliveryDraft.deliveryFees || 0
        : state.delivery?.fee ?? 0,
    );
    // Tax rate comes from /api/v1/tenant/tax-config (loaded once on app boot).
    // Falls back to 0 until the call resolves — see store/taxConfig.ts.
    const rate = useTaxConfig.getState().rate();
    // Taxable base includes the delivery fee, matching the server's
    // `taxableBase = value - discountValue + deliveryFee`.
    const vat = rm((afterDiscount + deliveryFee) * rate);
    // Returns refund VAT on what is refunded: the returned value less a pure
    // return's invoice-level discount (0 otherwise, so a return with no
    // discount computes exactly as before).
    const returnAfterDiscount = rm(returnSubtotal - returnDiscount);
    const returnVat = rm(returnAfterDiscount * rate);
    // The invoice's own total; points paid as a payment come off it below.
    const saleGross = rm(afterDiscount + deliveryFee + vat);
    const loyaltyPayment = redeem?.asPayment ? Math.min(rm(redeem.value), saleGross) : 0;
    // What is still to be paid on the sale leg.
    const saleTotal = rm(saleGross - loyaltyPayment);
    const returnTotal = rm(returnAfterDiscount + returnVat);
    const net = rm(saleTotal - returnTotal);

    const mode: 'sale' | 'return' | 'exchange' =
      saleLines.length > 0 && returnLines.length > 0 ? 'exchange'
        : returnLines.length > 0 ? 'return'
          : 'sale';

    const itemCount = lines.reduce((a, l) => a + l.qty, 0);
    return {
      subtotal,
      /** Money taken off individual lines — already excluded from `subtotal`. */
      lineDiscountTotal,
      discount: discountAmount,
      loyaltyDiscount,
      loyaltyPayment,
      saleGross,
      promotionDiscount,
      promotions: (quote?.promotions ?? []).filter((p) => p.level !== 'line'),
      ...linePromotions(saleLines, quote, promotionDiscount, rm),
      deliveryFee,
      afterDiscount,
      vat,
      // `total` stays the physically-settled net so existing consumers keep
      // working (for a plain sale, net === saleTotal).
      total: net,
      itemCount,
      returnSubtotal,
      returnDiscount,
      returnInheritedDiscount,
      returnVat,
      returnTotal,
      saleTotal,
      net,
      mode,
      hasReturns: returnLines.length > 0,
    };
  },
}));

/**
 * Item-level promotions per line, for DISPLAY (2026-10-07): the server books
 * them on the line's own discount, so the cart shows them there and drops their
 * named row. Capped at what the cart is actually taking off, so a stale quote
 * can never show more than the total does.
 */
function linePromotions(
  saleLines: ReadonlyArray<{ cartId: string }>,
  quote: CartState['promotionQuote'],
  promotionDiscount: number,
  rm: (n: number) => number,
): { promotionByLine: Record<string, number>; linePromotionTotal: number } {
  const shares = quote?.lineDiscounts ?? [];
  const promotionByLine: Record<string, number> = {};
  let total = 0;
  saleLines.forEach((l, i) => {
    const s = rm(Math.max(0, shares[i] ?? 0));
    if (s > 0 && total + s <= promotionDiscount + 1e-9) {
      promotionByLine[l.cartId] = s;
      total = rm(total + s);
    }
  });
  return { promotionByLine, linePromotionTotal: total };
}

/**
 * The cart's money figures, as a SUBSCRIBED value.
 *
 * `totals()` is a plain getter: it reads the store imperatively through
 * `get()`, so a component that selects the function (`useCart(s => s.totals)`)
 * and calls it during render subscribes to nothing at all. It re-renders only
 * when some *other* slice it happens to select changes, and recomputes the
 * figures then — one basket late.
 *
 * That is exactly how the promotion row misbehaved: the server's quote lands in
 * `promotionQuote`, nothing is subscribed to it, and the panel keeps painting
 * the previous total until the next add/remove happens to re-render it. Both
 * halves of the report — "the promotion only appears when I add another item"
 * and "removing an item leaves the old promotion up" — are the same missing
 * subscription.
 *
 * So: select every slice `totals()` derives from. Adding a new input to
 * `totals()` means adding it here too, or the figures go stale again.
 */
export function useCartTotals(): ReturnType<CartState['totals']> {
  const totals = useCart((s) => s.totals);
  useCart((s) => s.lines);
  useCart((s) => s.discount);
  useCart((s) => s.loyaltyRedeem);
  useCart((s) => s.promotionQuote);
  useCart((s) => s.delivery);
  useCart((s) => s.deliveryDraft);
  useCart((s) => s.orderMode);
  // totals() reads the tax rate off the tax-config store, which loads after
  // first paint — without this the very first cart of a session shows 0 VAT.
  useTaxConfig((s) => s.config);
  return totals();
}
